import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { adminFetch } from "@/lib/adminApi";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TeacherCommentReview() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [reportId, setReportId] = useState("");
  const [subjects, setSubjects] = useState(Array.from({ length: 8 }, () => ({ subject: "", grade: "" })));
  const [comments, setComments] = useState(["", "", "", ""]);
  const [photoId, setPhotoId] = useState("");
  const [mustIncludeVerified, setMustIncludeVerified] = useState(false);
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadOrder = async () => {
    const res = await adminFetch(`${API}/api/orders/${encodeURIComponent(orderNumber)}`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || `Order fetch failed (HTTP ${res.status})`);
    setOrder(data);
    return data;
  };

  useEffect(() => {
    loadOrder().catch((err) => setError(err.message || "Failed to load order"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber]);

  const reports = order?.reports || [];
  const report = useMemo(
    () => reports.find((item) => String(item.id) === String(reportId)) || reports[0] || null,
    [reports, reportId]
  );

  useEffect(() => {
    if (!report) return;
    setReportId(String(report.id));
    const creative = report.creative_output || {};
    const subjectSource = creative.final_subjects || creative.subject_candidates || [];
    setSubjects([0,1,2,3,4,5,6,7].map((i) => ({
      subject: subjectSource[i]?.subject || "",
      grade: subjectSource[i]?.grade || "",
    })));
    const commentSource = creative.final_teacher_comments || creative.teacher_comment_candidates || [];
    setComments([0,1,2,3].map((i) => {
      const item = commentSource[i];
      return typeof item === "string" ? item : item?.comment || "";
    }));
    const selected = (report.photos || []).find((photo) => photo.selected_for_report);
    setPhotoId(selected?.photo_id || "");
    setMustIncludeVerified(!report.questionnaire?.must_include);
    setMessage("");
    setError("");
  }, [report?.id]);

  const updateSubject = (index, key, value) => {
    setSubjects((current) => current.map((item, i) => i === index ? { ...item, [key]: value } : item));
  };

  const updateComment = (index, value) => {
    setComments((current) => current.map((item, i) => i === index ? value : item));
  };

  const selectPhoto = async (photo) => {
    if (!report || photoBusy || photoId === photo.photo_id) return;
    setPhotoBusy(true);
    setError("");
    setMessage("");
    try {
      if (photo.quality_status !== "Acceptable") {
        const reviewRes = await adminFetch(`${API}/api/reports/${report.id}/request-new-photos`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reason: `__PHOTO_REVIEW__|${photo.photo_id}|Acceptable` }),
        });
        const reviewData = await reviewRes.json().catch(() => ({}));
        if (!reviewRes.ok) throw new Error(reviewData.detail || `Photo selection failed (HTTP ${reviewRes.status})`);
      }

      const selectRes = await adminFetch(`${API}/api/reports/${report.id}/request-new-photos`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: `__SELECT_PRODUCTION_PHOTO__|${photo.photo_id}` }),
      });
      const selectData = await selectRes.json().catch(() => ({}));
      if (!selectRes.ok) throw new Error(selectData.detail || `Photo selection failed (HTTP ${selectRes.status})`);
      setPhotoId(photo.photo_id);
      await loadOrder();
      setMessage("Report photo selected.");
    } catch (err) {
      setError(err.message || "Photo selection failed");
    } finally {
      setPhotoBusy(false);
    }
  };

  const saveOrApprove = async () => {
    if (!report) return;
    if (subjects.some((item) => !item.subject.trim() || !item.grade.trim())) {
      setError("All 8 subjects and grades are required.");
      return;
    }
    if (comments.some((comment) => !comment.trim())) {
      setError("All 4 teacher comments are required.");
      return;
    }
    if (photoId && report.questionnaire?.must_include && !mustIncludeVerified) {
      setError("Verify the required customer content before approval.");
      return;
    }

    const creative = report.creative_output || {};
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const res = await adminFetch(`${API}/api/reports/${report.id}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          final_subjects: subjects.map((item) => ({ subject: item.subject.trim(), grade: item.grade.trim() })),
          final_teacher_comments: comments.map((comment) => comment.trim()),
          selected_photo_id: photoId,
          personalized_greeting: creative.personalized_greeting || `Dear ${report.pet_name},`,
          notes_text: creative.notes_text || "",
          award_wording: creative.award_wording || "Top Student Report!",
          approved_by: "Val",
          must_include_verified: Boolean(mustIncludeVerified),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Save failed (HTTP ${res.status})`);
      setMessage(photoId ? "Report content approved and locked for production." : "Report content saved.");
      await loadOrder();
    } catch (err) {
      setError(err.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (error && !order) return <div style={{ padding: 32, color: "#991B1B" }}>{error}</div>;
  if (!order) return <div style={{ padding: 32 }}>Loading report content…</div>;

  return (
    <div style={{ padding: 32, color: "#2A2540" }} data-testid="report-content-review">
      <Link to={`/admin/orders/${encodeURIComponent(orderNumber)}`} style={{ color: "#6A4FC0", fontWeight: 700, textDecoration: "none" }}>
        Back to order
      </Link>
      <h1 style={{ margin: "16px 0 4px", fontSize: 26 }}>Edit Report Content</h1>
      <div style={{ color: "#7A6E93", marginBottom: 20 }}>{order.order_number}</div>

      {reports.length > 1 && (
        <label style={{ display: "block", marginBottom: 18, fontWeight: 700 }}>
          Pet
          <select value={reportId} onChange={(e) => setReportId(e.target.value)} style={{ display: "block", marginTop: 6, padding: 10, minWidth: 260 }}>
            {reports.map((item) => <option key={item.id} value={item.id}>{item.pet_name}</option>)}
          </select>
        </label>
      )}

      <div style={{ maxWidth: 900, background: "#fff", border: "1px solid #ECEAF3", borderRadius: 14, padding: 20 }}>
        <h2 style={{ marginTop: 0 }}>{report?.pet_name}</h2>

        <h3 style={{ marginTop: 20 }}>Subjects and Grades</h3>
        {subjects.map((item, index) => (
          <div key={index} style={{ display: "grid", gridTemplateColumns: "1fr 140px", gap: 10, marginBottom: 10 }}>
            <input value={item.subject} onChange={(e) => updateSubject(index, "subject", e.target.value)} placeholder={`Subject ${index + 1}`} data-testid={`subject-${index + 1}`} style={{ padding: 10, border: "1px solid #D8D3E5", borderRadius: 10, fontFamily: "inherit", fontSize: 14 }} />
            <input value={item.grade} onChange={(e) => updateSubject(index, "grade", e.target.value)} placeholder="Grade" data-testid={`grade-${index + 1}`} style={{ padding: 10, border: "1px solid #D8D3E5", borderRadius: 10, fontFamily: "inherit", fontSize: 14 }} />
          </div>
        ))}

        <h3 style={{ marginTop: 26 }}>Teacher Comments</h3>
        {comments.map((comment, index) => (
          <label key={index} style={{ display: "block", marginBottom: 16, fontWeight: 700 }}>
            Teacher Comment {index + 1}
            <textarea value={comment} onChange={(e) => updateComment(index, e.target.value)} rows={2} data-testid={`teacher-comment-${index + 1}`} style={{ width: "100%", marginTop: 6, padding: 12, border: "1px solid #D8D3E5", borderRadius: 10, fontFamily: "inherit", fontSize: 14, resize: "vertical", boxSizing: "border-box" }} />
          </label>
        ))}

        <h3 style={{ marginTop: 26 }}>Choose Report Photo</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12, marginBottom: 16 }}>
          {(report?.photos || []).map((photo, index) => {
            const selected = photoId === photo.photo_id;
            return (
              <label key={photo.photo_id} style={{ position: "relative", border: selected ? "3px solid #6A4FC0" : "1px solid #D8D3E5", borderRadius: 10, padding: 8, cursor: photoBusy ? "wait" : "pointer", background: selected ? "#F5F2FF" : "#fff" }}>
                <input
                  type="checkbox"
                  checked={selected}
                  disabled={photoBusy}
                  onChange={() => selectPhoto(photo)}
                  aria-label={`Use Photo ${index + 1} for report`}
                  style={{ position: "absolute", top: 12, left: 12, width: 20, height: 20, zIndex: 2 }}
                />
                <img src={photo.file_url?.startsWith("http") ? photo.file_url : `${API}${photo.file_url}`} alt={`Photo ${index + 1}`} style={{ width: "100%", height: 145, objectFit: "cover", borderRadius: 8, display: "block" }} />
                <div style={{ marginTop: 7, fontSize: 13, fontWeight: 700 }}>Photo {index + 1}</div>
              </label>
            );
          })}
        </div>

        {report?.questionnaire?.must_include && (
          <label style={{ display: "flex", gap: 9, alignItems: "flex-start", marginBottom: 18, padding: 12, background: "#FEF3C7", borderRadius: 10 }}>
            <input type="checkbox" checked={mustIncludeVerified} onChange={(e) => setMustIncludeVerified(e.target.checked)} />
            <span>I verified that the required customer content is represented: <strong>{report.questionnaire.must_include}</strong></span>
          </label>
        )}

        {error && <div style={{ marginBottom: 12, color: "#991B1B" }}>{error}</div>}
        {message && <div style={{ marginBottom: 12, color: "#166534" }}>{message}</div>}

        <button type="button" onClick={saveOrApprove} disabled={busy || report?.report_status === "Production AI Processing"} data-testid="save-report-content" style={{ padding: "11px 18px", border: 0, borderRadius: 10, background: "#6A4FC0", color: "#fff", fontWeight: 800, cursor: busy ? "wait" : "pointer" }}>
          {busy ? "Saving…" : photoId ? "Approve and Continue to Production" : "Save Report Content"}
        </button>
      </div>
    </div>
  );
}
